import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search, MapPin, Store, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  fetchActiveDealers,
  DEALER_CATEGORY_LABELS,
  type Dealer,
} from "@/lib/dealers.functions";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { SmartImage } from "@/components/smart-image";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/community/dealers/")({
  head: () => ({
    meta: [
      { title: "UK Trading Card Dealer Directory | BidNova Community" },
      {
        name: "description",
        content:
          "Find trusted UK trading card dealers, hobby shops, grading services and card show organisers in the BidNova Community dealer directory.",
      },
      { property: "og:title", content: "UK Card Dealer Directory — BidNova Community" },
      {
        property: "og:description",
        content: "Trusted UK card shops, hobby stores and grading services.",
      },
      { property: "og:url", content: "https://bidnovaauctions.uk/community/dealers" },
      { property: "og:type", content: "website" },
      ...socialImageMeta(),
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/community/dealers" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Community", path: "/community" }, { name: "Dealers", path: "/community/dealers" }])],
  }),
  component: DealersIndex,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function DealersIndex() {
  const { t } = useTranslation("community");
  const { data = [], isLoading } = useQuery({
    queryKey: ["community", "dealers", "all"],
    queryFn: () => fetchActiveDealers(120),
    staleTime: 60_000,
  });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string | null>(null);
  const [game, setGame] = useState<string | null>(null);

  const { cats, games } = useMemo(() => {
    const c = new Set<string>();
    const g = new Set<string>();
    data.forEach((d) => {
      d.categories.forEach((x) => c.add(x));
      d.supported_games.forEach((x) => g.add(x));
    });
    return { cats: Array.from(c), games: Array.from(g) };
  }, [data]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return data.filter((d) => {
      if (cat && !d.categories.includes(cat)) return false;
      if (game && !d.supported_games.includes(game)) return false;
      if (!query) return true;
      return (
        d.name.toLowerCase().includes(query) ||
        (d.town ?? "").toLowerCase().includes(query) ||
        (d.city ?? "").toLowerCase().includes(query) ||
        (d.county ?? "").toLowerCase().includes(query) ||
        (d.postcode ?? "").toLowerCase().includes(query)
      );
    });
  }, [data, q, cat, game]);

  return (
    <div className="mx-auto max-w-7xl px-6 sm:px-8 pt-10 pb-16">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="font-display text-3xl text-white">{t("dealers.index.title", "Dealer Directory")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("dealers.index.subtitle", "Trusted UK card shops, hobby stores, grading services and online retailers.")}
          </p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("dealers.index.searchPlaceholder", "Search by name, town or postcode…")}
            aria-label={t("dealers.index.searchLabel", "Search dealers")}
            className="h-10 pl-9 bg-background/60 border-border/60"
          />
        </div>
      </div>

      {(cats.length > 0 || games.length > 0) && (
        <div className="mb-6 space-y-3">
          {cats.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground pr-2 self-center">{t("dealers.index.typeLabel", "Type")}</span>
              <FilterPill active={cat === null} onClick={() => setCat(null)}>{t("dealers.index.all", "All")}</FilterPill>
              {cats.map((c) => (
                <FilterPill key={c} active={cat === c} onClick={() => setCat(c)}>
                  {DEALER_CATEGORY_LABELS[c] ?? c}
                </FilterPill>
              ))}
            </div>
          )}
          {games.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground pr-2 self-center">{t("dealers.index.gameLabel", "Game")}</span>
              <FilterPill active={game === null} onClick={() => setGame(null)}>{t("dealers.index.all", "All")}</FilterPill>
              {games.map((g) => (
                <FilterPill key={g} active={game === g} onClick={() => setGame(g)}>
                  {g}
                </FilterPill>
              ))}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[220px] rounded-2xl border border-white/10 skeleton-shimmer" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center text-muted-foreground">
          {t("dealers.index.empty", "No dealers match your filters yet.")}
        </div>
      ) : (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((d) => <DealerCard key={d.id} d={d} />)}
        </div>
      )}
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
        active
          ? "border-primary/50 bg-primary/15 text-white"
          : "border-white/10 bg-white/[0.03] text-muted-foreground hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function DealerCard({ d }: { d: Dealer }) {
  const { t } = useTranslation("community");
  const loc = [d.town || d.city, d.county, d.postcode].filter(Boolean).join(", ");
  return (
    <Link
      to="/community/dealers/$slug"
      params={{ slug: d.slug }}
      className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_20px_50px_-20px_rgba(139,92,246,0.4)]"
    >
      <div className="flex items-start gap-4 p-5">
        <div className="grid h-14 w-14 place-items-center rounded-xl border border-white/10 bg-black/40 overflow-hidden shrink-0">
          {d.logo_url ? (
            <SmartImage src={d.logo_url} alt={`${d.name} logo`} width={56} height={56} widths={[56, 112]} className="h-full w-full object-cover" />
          ) : (
            <Store className="h-6 w-6 text-muted-foreground" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg text-white truncate">{d.name}</h3>
            {d.is_featured && (
              <span className="rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[10px] text-gold">
                {t("dealers.index.featured", "Featured")}
              </span>
            )}
          </div>
          {loc && (
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground line-clamp-1">
              <MapPin className="h-3 w-3" aria-hidden /> {loc}
            </p>
          )}
          {d.description && (
            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{d.description}</p>
          )}
        </div>
      </div>
      {(d.categories.length > 0 || d.supported_games.length > 0) && (
        <div className="mt-auto flex flex-wrap gap-1.5 px-5 pb-5">
          {d.categories.slice(0, 3).map((c) => (
            <span
              key={c}
              className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] text-muted-foreground"
            >
              {DEALER_CATEGORY_LABELS[c] ?? c}
            </span>
          ))}
          {d.supported_games.slice(0, 3).map((g) => (
            <span
              key={g}
              className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] text-primary"
            >
              {g}
            </span>
          ))}
        </div>
      )}
      {d.website_url && (
        <div className="border-t border-white/10 px-5 py-3 text-xs text-primary flex items-center gap-1.5">
          {t("dealers.index.visitWebsite", "Visit website")} <ExternalLink className="h-3 w-3" aria-hidden />
        </div>
      )}
    </Link>
  );
}
